import { Link } from 'react-router-dom'
import { Calendar, ArrowRight, Info, Droplet, Activity, Heart, Sparkles } from 'lucide-react'
import { scrapedData } from '../data'
import { renderFormattedText } from '../utils/textFormatter'
import SEO from '../components/SEO'

export default function NextSteps() {
  const { videreForlob } = scrapedData

  const reactions = [
    {
      icon: <Droplet className="w-6 h-6 text-[#eebb53]" />,
      title: "Drik rigeligt med vand",
      description: "Under behandlingen frigøres der affaldsstoffer fra musklerne, som skal skylles ud af kroppen. Vi anbefaler, at du drikker 1-2 liter vand ekstra på behandlingsdagen for at forebygge let hovedpine og støtte kroppens udrensning."
    },
    {
      icon: <Activity className="w-6 h-6 text-[#eebb53]" />,
      title: "Forvent let ømhed eller træthed",
      description: "Det er helt normalt at opleve en form for 'træningsømhed' eller dyb træthed i 1-2 dage efter behandlingen. Det er tegn på, at din krop arbejder, og at blodcirkulationen og helingsprocessen er sat i gang."
    },
    {
      icon: <Heart className="w-6 h-6 text-[#eebb53]" />,
      title: "Giv kroppen ro og hvile",
      description: "Undgå hård fysisk træning, tunge løft eller stressende aktiviteter umiddelbart efter din behandling. Lad kroppen bruge sin energi på at restituere og genfinde sin naturlige balance."
    },
    {
      icon: <Sparkles className="w-6 h-6 text-[#eebb53]" />,
      title: "Lyt til kroppens signaler",
      description: "Hver krop reagerer forskelligt. Nogle oplever øget energi med det samme, mens andre har brug for en lur. Begge reaktioner er sunde og naturlige. Giv dig selv lov til at mærke efter."
    }
  ]

  return (
    <div className="space-y-20 pb-16">
      <SEO
        title="Videre forløb & Behandlingsplan | Rørbækklinikken Viborg"
        description="Få overblik over dit behandlingsforløb hos Rørbækklinikken. Læs om restitution, hvor få behandlinger der oftest kræves, samt vores samarbejde med specialister."
      />
      
      {/* Page Header */}
      <section className="bg-[#F5F0E6]/40 py-16 border-b border-[#E6DFD3]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-4">
          <h1 className="font-serif text-4xl sm:text-5xl font-bold text-[#1E352F]">
            Videre forløb efter behandling
          </h1>
          <p className="text-base sm:text-lg text-[#1E352F]/80 leading-relaxed">
            Behandlingen stopper ikke, når du forlader briksen. Her kan du læse om, hvordan din krop reagerer, og hvordan vi sammen planlægger det bedste forløb for dig.
          </p>
        </div>
      </section>

      {/* Course Planning (Verbatim Scraped Content) */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          
          {/* Left: Verbatim Text */}
          <div className="lg:col-span-7 space-y-6">
            <h2 className="font-serif text-3xl font-bold text-[#1E352F]">
              {videreForlob.title}
            </h2>
            <div className="space-y-4 text-sm sm:text-base text-[#1E352F]/80 leading-relaxed">
              {renderFormattedText(videreForlob.content)}
            </div>
          </div>

          {/* Right: Info Box & Image */}
          <div className="lg:col-span-5 space-y-6">
            <div className="relative rounded-2xl overflow-hidden shadow-md border border-[#E6DFD3] aspect-[4/3]">
              <img
                src="https://assets.auxodev.dk/assets/bd5a76fd-d82e-431d-9e97-5b07d543e9b6/67505f7b-59a8-4ba6-9bdc-c9e8a91e8d74.jpg"
                alt="Klient slapper af efter behandling"
                className="w-full h-full object-cover"
              />
            </div>

            <div className="bg-[#F5F0E6] p-5 rounded-xl border border-[#E6DFD3] flex gap-3.5 items-start">
              <Info className="w-5 h-5 text-[#eebb53] shrink-0 mt-0.5" />
              <p className="text-xs sm:text-sm text-[#1E352F]/80 leading-relaxed">
                <strong>Bemærk:</strong> Hvis du oplever kraftige eller uventede reaktioner, eller hvis du blot er i tvivl om noget efter din behandling, er du altid velkommen til at kontakte os på tlf. 86 63 93 93. Vi følger dig gerne helt til dørs.
              </p>
            </div>
          </div>

        </div>
      </section>

      {/* Body Reactions */}
      <section className="bg-[#FDFBF7] py-16 border-y border-[#E6DFD3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
          
          <div className="text-center max-w-2xl mx-auto space-y-3">
            <h2 className="font-serif text-3xl font-bold text-[#1E352F]">
              Hvordan reagerer kroppen efter behandlingen?
            </h2>
            <p className="text-sm text-[#1E352F]/70">
              Når vi arbejder dybdegående med dine muskler, led og nervesystem, sætter vi gang i en række fysiologiske processer. Det er vigtigt at forstå, at disse reaktioner er positive tegn på, at kroppen har taget imod behandlingen og er begyndt at regulere sig selv.
            </p>
          </div>

          {/* Reactions Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {reactions.map((react, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl p-6 sm:p-8 border border-[#E6DFD3] shadow-sm flex gap-5 items-start"
              >
                <div className="p-3 rounded-lg bg-[#F5F0E6] shrink-0">
                  {react.icon}
                </div>
                <div className="space-y-2">
                  <h3 className="font-serif text-lg font-bold text-[#1E352F]">
                    {react.title}
                  </h3>
                  <p className="text-sm text-[#1E352F]/70 leading-relaxed">
                    {react.description}
                  </p>
                </div>
              </div>
            ))}
          </div>

        </div>
      </section>

      {/* CTA Section */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="bg-[#1E352F] rounded-2xl text-[#F5F0E6] p-8 sm:p-12 text-center space-y-6 border border-[#2D4A43]">
          <h2 className="font-serif text-2xl sm:text-3xl font-bold text-white">
            Vil du starte dit behandlingsforløb i dag?
          </h2>
          <p className="text-sm sm:text-base text-[#F5F0E6]/80 max-w-2xl mx-auto leading-relaxed">
            Book din første tid online via Planway. Vi tager os god tid til at lytte og lægge den helt rigtige plan for dig.
          </p>
          <div className="pt-2">
            <a
              href="https://roerbaekklinikken.planway.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#eebb53] hover:bg-[#dca63d] text-white px-8 py-3.5 rounded-full text-base font-semibold transition-colors shadow-sm"
            >
              <Calendar className="w-5 h-5" />
              Book din første tid online
            </a>
          </div>
        </div>
      </section>

    </div>
  )
}
